// ---------------------------------------------------------------------------
// Narration A/B — the operator-only view over `diagnostics.narration_shadow`.
//
// The orchestrator authors the delivered voice-over once, then runs the SAME
// refiner prompt through a second, differently-routed model and records both
// texts. Neither this module nor anything downstream of it can change an Echo:
// the shadow lines exist only in diagnostics, and they are never part of
// `audio_content`. This turns that record into two comparable columns.
//
// Pairing rule: lines are matched by their narration window when both sides
// carry one (the NSB `window_id`, the only join key between words and
// pictures), and by position otherwise — so legacy and window-bound echoes
// both read sensibly.
// ---------------------------------------------------------------------------

type EnvelopeInput = Record<string, unknown> | undefined;

export const NARRATION_AB_SCHEMA_VERSION = "narration-shadow.v1";

export type NarrationAbStatus = "ok" | "failed" | "timeout" | "empty";

export type NarrationAbLine = {
  startSecond: number | null;
  windowId: number | null;
  text: string;
};

export type NarrationAbLane = {
  /** Model id as reported by the provider, e.g. "claude-opus-4-6". */
  model: string;
  /** True only for the lane whose words the customer actually hears. */
  delivered: boolean;
  lineCount: number;
  wordCount: number;
  lines: NarrationAbLine[];
  /** Signed route id; present on the shadow lane only. */
  route: string | null;
};

/** One row of the side-by-side table: the same beat, written twice. */
export type NarrationAbRow = {
  key: string;
  label: string;
  primary: NarrationAbLine | null;
  shadow: NarrationAbLine | null;
};

export type NarrationAbComparison = {
  status: NarrationAbStatus;
  reason: string | null;
  elapsedMs: number | null;
  primary: NarrationAbLane;
  shadow: NarrationAbLane;
  rows: NarrationAbRow[];
};

export const NARRATION_AB_STATUS_COPY: Record<NarrationAbStatus, string> = {
  ok: "Second opinion recorded",
  empty: "The second model returned no lines",
  failed: "The second model failed — nothing to compare",
  timeout: "The second model timed out — nothing to compare",
};

export function summarizeNarrationAb(
  envelope: EnvelopeInput,
): NarrationAbComparison | null {
  const record = recordValue(
    recordValue(envelope?.diagnostics)?.narration_shadow,
  );
  if (!record) return null;

  const primary = laneOf(recordValue(record.primary), true);
  const shadow = laneOf(recordValue(record.shadow), false);
  if (!primary && !shadow) return null;

  const primaryLane = primary ?? emptyLane(true);
  const shadowLane = shadow ?? emptyLane(false);
  return {
    status: statusOf(record.status),
    reason: stringValue(record.reason),
    elapsedMs: numberValue(record.elapsed_ms),
    primary: primaryLane,
    shadow: shadowLane,
    rows: pairLines(primaryLane.lines, shadowLane.lines),
  };
}

/**
 * Pair the two lanes into rows.
 *
 * Window ids are authoritative when both sides use them: two writers can
 * choose a different NUMBER of lines for the same film, so pairing by index
 * alone would silently compare unrelated beats.
 */
export function pairLines(
  primaryLines: NarrationAbLine[],
  shadowLines: NarrationAbLine[],
): NarrationAbRow[] {
  const bothWindowed =
    primaryLines.length > 0 &&
    shadowLines.length > 0 &&
    primaryLines.every((line) => line.windowId !== null) &&
    shadowLines.every((line) => line.windowId !== null);

  if (!bothWindowed) {
    const length = Math.max(primaryLines.length, shadowLines.length);
    return Array.from({ length }, (_unused, index) => ({
      key: `row-${index}`,
      label: `Line ${index + 1}`,
      primary: primaryLines[index] ?? null,
      shadow: shadowLines[index] ?? null,
    }));
  }

  const windows: number[] = [];
  for (const line of [...primaryLines, ...shadowLines]) {
    if (line.windowId !== null && !windows.includes(line.windowId)) {
      windows.push(line.windowId);
    }
  }
  windows.sort((left, right) => left - right);

  return windows.map((windowId) => ({
    key: `window-${windowId}`,
    label: `Window ${windowId}`,
    primary: primaryLines.find((line) => line.windowId === windowId) ?? null,
    shadow: shadowLines.find((line) => line.windowId === windowId) ?? null,
  }));
}

function laneOf(
  value: Record<string, unknown> | null,
  delivered: boolean,
): NarrationAbLane | null {
  if (!value) return null;
  const lines = linesOf(value.lines);
  return {
    model: stringValue(value.model) ?? "unknown",
    delivered:
      typeof value.delivered === "boolean" ? value.delivered : delivered,
    lineCount: numberValue(value.line_count) ?? lines.length,
    wordCount: numberValue(value.word_count) ?? countWords(lines),
    lines,
    route: stringValue(value.route),
  };
}

function emptyLane(delivered: boolean): NarrationAbLane {
  return {
    model: "unknown",
    delivered,
    lineCount: 0,
    wordCount: 0,
    lines: [],
    route: null,
  };
}

function linesOf(value: unknown): NarrationAbLine[] {
  if (!Array.isArray(value)) return [];
  const lines: NarrationAbLine[] = [];
  for (const entry of value) {
    const record = recordValue(entry);
    const text = stringValue(record?.text);
    if (!record || !text) continue;
    lines.push({
      startSecond: numberValue(record.start_second),
      windowId: numberValue(record.window_id),
      text,
    });
  }
  return lines;
}

function countWords(lines: NarrationAbLine[]): number {
  return lines.reduce(
    (total, line) => total + line.text.split(/\s+/).filter(Boolean).length,
    0,
  );
}

function statusOf(value: unknown): NarrationAbStatus {
  return value === "ok" ||
    value === "failed" ||
    value === "timeout" ||
    value === "empty"
    ? value
    : "failed";
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
